import * as React from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

const toneClasses: Record<string, string> = {
  default:
    'border-border text-muted-foreground hover:border-primary/40 hover:bg-primary/10 hover:text-primary',
  primary:
    'border-border text-muted-foreground hover:border-primary/40 hover:bg-primary/10 hover:text-primary',
  success:
    'border-emerald-500/30 text-emerald-600 hover:border-emerald-500/50 hover:bg-emerald-500/10 hover:text-emerald-500 dark:text-emerald-400',
  danger:
    'border-red-500/30 text-red-600 hover:border-red-500/50 hover:bg-red-500/10 hover:text-red-500 dark:text-red-400',
  warning:
    'border-amber-500/30 text-amber-600 hover:border-amber-500/50 hover:bg-amber-500/10 hover:text-amber-500 dark:text-amber-400',
  info: 'border-blue-500/30 text-blue-600 hover:border-blue-500/50 hover:bg-blue-500/10 hover:text-blue-500 dark:text-blue-400',
}

export interface IconActionButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: React.ReactNode
  tooltip: string
  tone?: keyof typeof toneClasses
  to?: string
  asChild?: boolean
  delayDuration?: number
  className?: string
}

export function IconActionButton({
  icon,
  tooltip,
  tone = 'default',
  to,
  asChild,
  onClick,
  disabled,
  delayDuration = 150,
  className,
  'aria-label': ariaLabel,
  ...props
}: IconActionButtonProps) {
  const toneClass = toneClasses[tone] ?? toneClasses.default

  const button = (
    <Button
      type={to ? undefined : 'button'}
      variant="ghost"
      size="icon-sm"
      disabled={disabled}
      onClick={onClick}
      className={cn(toneClass, className)}
      aria-label={ariaLabel ?? tooltip}
      {...props}
      asChild={asChild || Boolean(to)}
    >
      {to ? (
        <Link to={to}>{icon}</Link>
      ) : (
        <span className="flex items-center justify-center">{icon}</span>
      )}
    </Button>
  )

  return (
    <TooltipProvider delayDuration={delayDuration}>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
